import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {Box, Button, CircularProgress, Divider, MenuItem, Paper, Popover, SvgIcon, TextField, Typography} from "@mui/material";
import {AutoFixHigh, Edit} from "@mui/icons-material";
import {Row} from "reactstrap";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import SearchAutoComplete from "../../../../../../../../components/base/auto_complete/search_auto_complete";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../../../../core/models/static/routes/app-routes";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import {ReactComponent as RemoveIcon} from "../../../../../../../../../assets/images/remove.svg";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import classnames from "classnames";
import {QuoteInformationViewProductsDataGridColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import SaveOrRollback from "../../../../../../../../components/app-specific/save-or-rollback";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import ProductInformationPopover from "../../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import ImportProductsWithAIDialog from "../../../../../../../../components/dialogs/import-products-with-ai-dialog";
import {useHistory, useParams} from "react-router-dom";
import {usePartFormQualificationGate} from "../../../../../../../../features/part-form-qualification";
import {
    createQuoteTarget,
    getQuoteOrderPendingKey,
    SuggestiveSellingSectionPending,
    useSuggestiveSelling,
} from "../../../../../../../../features/suggestive-selling";
import EnvService from "../../../../../../../../../core/services/env";
import {generateWarehouseName} from "../../../../../../../../../core/services/utils/name-generator";

const dataGridSaveConfig = {
    name: 'QuoteInformationViewProductsSectionDataGrid',
    version: 4,
}

const initialNewProduct = {product: null, warehouse: null, quantity: '1'};
const initialImportWithAiDialog = {open: false, warehouses: null, onImport: () => void 0};

const QuoteInformationViewProductsSection = ({
                                                 order = {},
                                                 setOrder,
                                                 orderLoading,
                                                 warehouses: _warehouses,
                                                 canEditUnitPrice,
                                                 productAvailabilities,
                                                 getProductsAvailabilities,
                                                 setSubmittingDisabled,
                                                 eligiblePriceChangePct,
                                             }) => {
    const params = useParams();
    const history = useHistory();
    const [updating, setUpdating] = useState(false);
    const [creatingItemInSelectedWarehouse, setCreatingItemInSelectedWarehouse] = useState([]);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const [newProduct, setNewProduct] = useState(initialNewProduct);
    const [importWithAiDialog, setImportWithAiDialog] = useState(initialImportWithAiDialog);
    const [v2Warehouses, setV2Warehouses] = useState([]);

    const warehouses = useMemo(() => EnvService.enableDemoNameMasking
        ? _warehouses?.map(warehouse => ({
            ...warehouse,
            name: generateWarehouseName(warehouse.name),
        }))
        : _warehouses, [_warehouses]);


    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const customerId = useMemo(() => Number(params.customerId), [params.customerId])
    const {checking: checkingFormQualification, ensureAllQualified, GateDialogs} = usePartFormQualificationGate();
    const {offerAfterAdd} = useSuggestiveSelling();
    const quotePendingKey = useMemo(() => getQuoteOrderPendingKey(orderId), [orderId]);

    const quoteSalesOptions = useMemo(() => ({
        allowDuplicatePartNumbersInOrders: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders,
        manageWarehousesInQuote: operatorAccessLevels.packages.sales.manageWarehousesInQuote,
    }), [
        operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders,
        operatorAccessLevels.packages.sales.manageWarehousesInQuote,
    ]);

    /**
     * Loads v2 warehouses when the operator can issue credit, for negative-quantity warehouse assignment.
     */
    useEffect(() => {
        if (!operatorAccessLevels.packages.sales.issueCredit) {
            return;
        }
        const loadV2Warehouses = async () => {
            const response = await Api.readAllWarehouses();
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setV2Warehouses((response.data ?? []).map((warehouse) => ({
                    ...warehouse,
                    name: warehouse.title ?? warehouse.name,
                    title: warehouse.title ?? warehouse.name,
                })));
            }
        };
        void loadV2Warehouses();
    }, [isMounted, operatorAccessLevels.packages.sales.issueCredit]);

    /**
     * Returns salesperson warehouses, or v2 warehouses when the quantity is a credit (negative).
     */
    const getWarehouseOptionsForQuantity = useCallback((quantity) => {
        if (operatorAccessLevels.packages.sales.issueCredit && Number(quantity) < 0 && v2Warehouses.length) {
            return v2Warehouses;
        }
        return warehouses;
    }, [operatorAccessLevels.packages.sales.issueCredit, v2Warehouses, warehouses]);

    const buildQuoteTarget = useCallback((warehouseCode) => createQuoteTarget({
        customerId,
        orderId,
        warehouseCode,
        getOrderItems: () => order?.items ?? [],
        onOrderUpdated: setOrder,
        salesOptions: quoteSalesOptions,
    }), [customerId, order?.items, orderId, quoteSalesOptions, setOrder]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(
                !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes
                    ? [{
                        name: QuoteInformationViewProductsDataGridColumnNames.remove,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        pinned: true,
                        sortable: false,
                        pinnedToggleable: false,
                        resizable: false,
                        visibilityToggleable: false,
                        width: 0,
                    }]
                    : []
            ),
            {
                title: "Part No",
                name: QuoteInformationViewProductsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
            },
            {
                title: "Warehouse",
                name: QuoteInformationViewProductsDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
                width: 150,
            },
            {
                title: "Description",
                name: QuoteInformationViewProductsDataGridColumnNames.description,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 250,
            },
            ...(
                !order?.appliedGiftCardInfo
                    ? [{
                        title: "Discount",
                        name: QuoteInformationViewProductsDataGridColumnNames.discount,
                        alignment: DataGridColumnAlignments.right,
                        type: DataGridColumnTypes.element,
                        sortable: false,
                        width: 120,
                    }]
                    : []
            ),
            {
                title: "Order Quantity",
                name: QuoteInformationViewProductsDataGridColumnNames.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 150,
            },
            ...(
                !order?.isClosed
                    ? [{
                        title: "Available Quantity",
                        name: QuoteInformationViewProductsDataGridColumnNames.availableQuantity,
                        alignment: DataGridColumnAlignments.right,
                        type: DataGridColumnTypes.string,
                        sortable: false,
                        width: 140,
                    }]
                    : []
            ),
            {
                title: "Unit Price",
                name: QuoteInformationViewProductsDataGridColumnNames.unitPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Total Price",
                name: QuoteInformationViewProductsDataGridColumnNames.totalPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            ...(EnvService.DEV_showOrderProductComments
                ? [{
                    title: "Comment",
                    name: QuoteInformationViewProductsDataGridColumnNames.comment,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 220,
                }]
                : []),
        ], [order?.isClosed, order?.appliedGiftCardInfo, operatorAccessLevels.packages.sales.manageQuotes])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**
     * Removes an item from this order by calling the api.
     *
     * - if the result of the api is successful, removes the item from the list.
     * - if the order is deleted after the removal, navigates the user back to the list view
     * @type {(item: any) => Promise<void>}
     */
    const removeItemFormOrder = useCallback(async (item) => {
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            partNumbersRemoveList: [item.partNo]
        };
        setUpdating(true);
        setSubmittingDisabled(true);
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
            if (response.data.isDeleted) {
                history.replace(routeFunctions.customer.sales.quotes.base(params.customerId));
            }
        }
    }, [isMounted, orderId, params.customerId, setOrder, customerId, history, setSubmittingDisabled])

    /**
     * Adds a new product to the items of the current order by calling the api.
     * - if the result of the api is successful, updates the list.
     * @type {(product: any, warehouse: any) => Promise<void>}
     */
    const addProductToOrder = useCallback(async (product, warehouse) => {
        if (!product)
            return;
        const canProceed = await ensureAllQualified(customerId, [{
            partId: product.id,
            partNumber: product.partNumber,
            partLabel: product.partNumber,
        }]);
        if (!canProceed) {
            return;
        }
        const existingItem = order?.items?.find(e => e.partNo === product.partNumber);
        const existingItemQty = (Number(existingItem?.orderQty ?? '0'))
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: [
                {
                    partNo: product.partNumber,
                    finalCount: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders
                        ? 1
                        : existingItemQty + (1 * (existingItemQty < 0) ? -1 : 1),
                    unitPrice: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders ? null : existingItem?.unitPriceBeforeDiscount,
                    ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                        whs: warehouse?.code,
                    }),
                    partDescription: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders ? null : existingItem?.description,
                }
            ]
        };
        setUpdating(true);
        setSubmittingDisabled(true);
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
            await offerAfterAdd({
                sourcePartId: product.id,
                sourcePartNumber: product.partNumber,
                sourceQuantity: 1,
                pendingKey: quotePendingKey,
                target: buildQuoteTarget(warehouse?.code),
            });
        }
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
    }, [buildQuoteTarget, customerId, ensureAllQualified, offerAfterAdd, order?.items, orderId, operatorAccessLevels.packages.sales.manageWarehousesInQuote, quotePendingKey, setSubmittingDisabled, isMounted, setOrder, operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders])

    /**
     * Adds a new part to the items of the current order by calling the api.
     * @type {(setLoading: (loading: boolean) => void) => Promise<void>}
     */
    const addNewProductToOrder = useCallback(async (setLoading) => {
        const canProceed = await ensureAllQualified(customerId, [{
            partId: newProduct?.product?.id,
            partNumber: newProduct?.product?.partNumber,
            partLabel: newProduct?.product?.partNumber,
        }]);
        if (!canProceed) {
            return;
        }
        setUpdating(true);
        setLoading?.(true);
        setSubmittingDisabled(true);
        const existingItem = order?.items?.find(e => e.partNo === newProduct?.product?.partNumber);
        const existingItemQty = (Number(existingItem?.orderQty ?? '0'))
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: [
                {
                    partNo: newProduct?.product?.partNumber,
                    finalCount: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders
                        ? newProduct?.quantity
                        : existingItemQty + newProduct?.quantity,
                    ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                        whs: newProduct?.warehouse?.code,
                    }),
                    unitPrice: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders ? null : existingItem?.unitPriceBeforeDiscount,
                    partDescription: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders ? null : existingItem?.description,
                }
            ]
        };
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
            setNewProduct(initialNewProduct);
            if (newProduct?.product?.id) {
                await offerAfterAdd({
                    sourcePartId: newProduct.product.id,
                    sourcePartNumber: newProduct.product.partNumber,
                    sourceQuantity: newProduct?.quantity,
                    pendingKey: quotePendingKey,
                    target: buildQuoteTarget(newProduct?.warehouse?.code),
                });
            }
        }
        if (!isMounted())
            return;
        setLoading?.(false);
        setUpdating(false);
        setSubmittingDisabled(false);
    }, [buildQuoteTarget, customerId, ensureAllQualified, offerAfterAdd, setSubmittingDisabled, order?.items, orderId, newProduct?.product?.id, newProduct?.product?.partNumber, newProduct?.quantity, newProduct?.warehouse?.code, operatorAccessLevels.packages.sales.manageWarehousesInQuote, quotePendingKey, isMounted, setOrder, operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders])

    /**
     * Imports the products to the current order by calling the api.
     * @type {(function(*, *): Promise<void>)}
     */
    const importProductsToOrder = useCallback(async (products, replace) => {
        if (!products?.length)
            return;
        const canProceed = await ensureAllQualified(
            customerId,
            products.map((e) => ({
                partNumber: e.partNo,
                partLabel: e.partNo,
            })),
        );
        if (!canProceed) {
            return;
        }
        setUpdating(true);
        setSubmittingDisabled(true);
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: products.map(e => {
                const existingItem = order?.items?.find(i => i.partNo === e.partNo);
                const existingItemQty = (Number(existingItem?.orderQty ?? '0'))
                return ({
                    partNo: e.partNo,
                    finalCount: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders || replace
                        ? e.quantity
                        : existingItemQty + e.quantity,
                    // TODO: if we also want to import the product price, use e.price ?? existingItem?.unitPriceBeforeDiscount,
                    unitPrice: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders || replace ? null : existingItem?.unitPriceBeforeDiscount,
                    ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                        whs: e.warehouseCode,
                    }),
                    partDescription: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders || replace ? null : existingItem?.description,
                })
            })
        };
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
            const lastProduct = products[products.length - 1];
            if (lastProduct?.partNo) {
                const partInfoResponse = await Api.getPartInfoWithPartNumber({partNumbers: [lastProduct.partNo]}, false);
                const partId = partInfoResponse?.data?.find((e) => e.partNo === lastProduct.partNo)?.id;
                if (partId) {
                    await offerAfterAdd({
                        sourcePartId: partId,
                        sourcePartNumber: lastProduct.partNo,
                        sourceQuantity: lastProduct.quantity,
                        pendingKey: quotePendingKey,
                        target: buildQuoteTarget(lastProduct.warehouseCode),
                    });
                }
            }
        }
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
    }, [buildQuoteTarget, customerId, ensureAllQualified, offerAfterAdd, orderId, isMounted, order?.items, quotePendingKey, setOrder, setSubmittingDisabled, operatorAccessLevels.packages.sales.manageWarehousesInQuote, operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders])

    /**
     * Updates the quantity of an item for the current order by calling the api.
     *
     * - if the result of the api is successful then updates the list.
     * @type {(item: any, newValue: string) => Promise<boolean>}
     */
    const updateQuantity = useCallback(async (item, newValue) => {
        setUpdating(true);
        setSubmittingDisabled(true);
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: [
                {
                    partNo: item.partNo,
                    finalCount: operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(newValue) : parseInt(newValue),
                    unitPrice: item.unitPriceBeforeDiscount,
                    ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                        whs: item.whse,
                    }),
                    partDescription: item.description,
                }
            ]
        };
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        setUpdating(false);
        setSubmittingDisabled(false);
        return response?.resultFlag;
    }, [isMounted, orderId, customerId, setOrder, setSubmittingDisabled, operatorAccessLevels.packages.sales.manageWarehousesInQuote, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    /**
     * Updates the warehouse of an item for the current order by calling the api.
     * @type {function(*, *): Promise<boolean>}
     */
    const updateWarehouse = useCallback(async (item, newValue) => {
        setUpdating(true);
        setSubmittingDisabled(true);
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: [
                {
                    partNo: item.partNo,
                    finalCount: Number(item.orderQty),
                    unitPrice: item.unitPriceBeforeDiscount,
                    whs: newValue.code,
                    partDescription: item.description,
                }
            ]
        };
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        setSubmittingDisabled(false);
        setUpdating(false);
        return response?.resultFlag;
    }, [isMounted, orderId, setOrder, customerId, setSubmittingDisabled])

    /**
     * Updates the warehouse of an item for the current order by calling the api.
     * @type {function(*, *): Promise<boolean>}
     */
    const updateDescription = useCallback(async (item, newValue) => {
        setUpdating(true);
        setSubmittingDisabled(true);
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: [
                {
                    partNo: item.partNo,
                    finalCount: Number(item.orderQty),
                    unitPrice: item.unitPriceBeforeDiscount,
                    whs: item.whse,
                    partDescription: newValue,
                }
            ]
        };
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        setUpdating(false);
        setSubmittingDisabled(false);
        return response?.resultFlag;
    }, [isMounted, orderId, setOrder, customerId, setSubmittingDisabled])

    /**
     * Updates the unit price of the item.
     * @type {function(*, *): Promise<boolean>}
     */
    const updateUnitPrice = useCallback(async (item, newValue) => {
        setUpdating(true);
        setSubmittingDisabled(true);
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            PartCountChangedList: [
                {
                    partNo: item.partNo,
                    finalCount: Number(item.orderQty),
                    unitPrice: newValue,
                    ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                        whs: item.whse,
                    }),
                    partDescription: item.description,
                }
            ]
        };
        const response = await Api.updateQuoteItems(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        setUpdating(false);
        setSubmittingDisabled(false);
        return response?.resultFlag;
    }, [isMounted, orderId, setOrder, customerId, setSubmittingDisabled, operatorAccessLevels.packages.sales.manageWarehousesInQuote])

    /**
     * Updates the discount of an item for the current order by calling the api.
     *
     * - if the result of the api is successful then updates the list.
     * @type {(item: any, newValue: string) => Promise<boolean>}
     */
    const updateDiscount = useCallback(async (item, newValue) => {
        setUpdating(true);
        setSubmittingDisabled(true);
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            discountPercentage: Number(newValue),
            partNo: item.partNo,
        };
        const response = await Api.updateQuoteItemDiscount(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        setUpdating(false);
        setSubmittingDisabled(false);
        return response?.resultFlag;
    }, [isMounted, orderId, customerId, setOrder, setSubmittingDisabled])

    /**
     * Updates the comment of a quote line item.
     * @type {(item: any, newValue: string) => Promise<boolean|undefined>}
     */
    const updateItemComment = useCallback(async (item, newValue) => {
        setUpdating(true);
        setSubmittingDisabled(true);
        const response = await Api.updateQuoteItemComment({
            customerId,
            orderId,
            partNo: item.partNo,
            orderPartId: item.id,
            comment: newValue?.trim() ?? '',
        });
        if (!isMounted())
            return;
        setUpdating(false);
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        return response?.resultFlag;
    }, [customerId, orderId, isMounted, setOrder, setSubmittingDisabled])

    /**
     * Gets users confirmation before removing the current order item.
     * @type {(item: any) => void}
     */
    const getRemoveConfirmation = useCallback((item) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <div className={'text-sm text-color-2'}>
                    Are you sure that you want to remove the following item from this order?
                </div>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeItemFormOrder(item),
        );
    }, [removeItemFormOrder])

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName].
     *
     * @type {(rowKey: any, colName: any, newValue: any) => Promise<void>}
     */
    const onCellEdited = useCallback(async (_, newValue, rowKey, colName) => {
        switch (colName) {
            case QuoteInformationViewProductsDataGridColumnNames.discount:
                return updateDiscount(order?.items?.find(e => e.id === rowKey), newValue);
            case QuoteInformationViewProductsDataGridColumnNames.quantity:
                return updateQuantity(order?.items?.find(e => e.id === rowKey), newValue);
            case QuoteInformationViewProductsDataGridColumnNames.warehouse:
                return updateWarehouse(order?.items?.find(e => e.id === rowKey), newValue);
            case QuoteInformationViewProductsDataGridColumnNames.description:
                return updateDescription(order?.items?.find(e => e.id === rowKey), newValue);
            case QuoteInformationViewProductsDataGridColumnNames.unitPrice:
                return updateUnitPrice(order?.items?.find(e => e.id === rowKey), newValue);
            case QuoteInformationViewProductsDataGridColumnNames.comment:
                return updateItemComment(order?.items?.find(e => e.id === rowKey), newValue);
            default:
                return;
        }
    }, [order?.items, updateDescription, updateDiscount, updateItemComment, updateQuantity, updateUnitPrice, updateWarehouse])

    /**
     * Changes the value of the new product quantity.
     * @type {(event: any) => void}
     */
    const onNewProductQuantityChanged = useCallback((event) => {
        const value = event.target.value;
        let parsed = Number(value);
        if (!value || Number.isNaN(parsed)) {
            return setNewProduct((prev) => ({...prev, quantity: null}));
        }
        if (!operatorAccessLevels.packages.sales.issueCredit) {
            parsed = Math.max(0, parsed);
        }
        setNewProduct((prev) => ({...prev, quantity: parsed}));
    }, [operatorAccessLevels.packages.sales.issueCredit])

    /**
     * Opens the import with AI dialog.
     * @type {(function(): void)}
     */
    const openImportWithAiDialog = useCallback(() => {
        setImportWithAiDialog({
            open: true,
            close: () => setImportWithAiDialog(initialImportWithAiDialog),
            warehouses: warehouses,
            onImport: importProductsToOrder,
        })
    }, [importProductsToOrder, warehouses]);

    /**
     * Callback function to be called when a new product is selected.
     * @type {(part: any, warehouse: any) => void}
     */
    const onNewProductSelected = useCallback((part, warehouse) => setNewProduct(prevState => ({
        ...prevState,
        product: part,
        warehouse: warehouse,
    })), [])

    /**
     * Creates an item in the selected warehouse for the provided item.
     * @type {(function(*, *): Promise<void>)}
     */
    const createItemInSelectedWarehouse = useCallback(async (item, warehouse) => {
        setCreatingItemInSelectedWarehouse(prevState => [...prevState, item.partNo]);
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: [item.partNo],
        });
        if (!isMounted())
            return;
        if (!partInfoResponse?.resultFlag) {
            setCreatingItemInSelectedWarehouse(prevState => prevState.filter(e => e !== item.partNo));
            return;
        }
        const forApi = {
            partId: partInfoResponse.data?.find(e => e.partNo === item.partNo)?.id,
            warehouseId: warehouse.id,
        }
        const response = await Api.createPartInWarehouse(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await updateWarehouse(item, warehouse);
            await getProductsAvailabilities([item.partNo]);
        }
        if (!isMounted())
            return;
        setCreatingItemInSelectedWarehouse(prevState => prevState.filter(e => e !== item.partNo));
    }, [getProductsAvailabilities, isMounted, updateWarehouse])


    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        let items = order?.items ?? [];
        if (order?.isClosed === false && !updating && operatorAccessLevels.packages.sales.manageQuotes) {
            items = [...items, {id: '__add-product',}]
        }
        return items.map(item => {
            const isNewProduct = item.id === '__add-product';
            const warehouseEditable = !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes && operatorAccessLevels.packages.sales.manageWarehousesInQuote;
            const discountEditable = !order?.isClosed && !!item?.discountable && operatorAccessLevels.packages.sales.manageQuotes;
            const quantityEditable = !order?.isClosed && item.partNo !== order?.appliedGiftCardInfo?.equalPartNo && operatorAccessLevels.packages.sales.manageQuotes;
            const descriptionEditable = !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes;
            const unitPriceEditable = !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes && operatorAccessLevels.packages.sales.manageUnitPriceInQuote && canEditUnitPrice;
            const commentEditable = EnvService.DEV_showOrderProductComments && !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes;
            const lineWarehouseOptions = getWarehouseOptionsForQuantity(isNewProduct ? newProduct.quantity : item.orderQty);
            const warehouse = lineWarehouseOptions?.find(e => e.code === item?.whse)
                ?? warehouses?.find(e => e.code === item?.whse);
            const availability = productAvailabilities[item.partNo]?.availability?.find(e => e.warehouseCode === item.whse);
            const warehouseCreatable = !!warehouse && operatorAccessLevels.packages.sales.manageQuotes && operatorAccessLevels.packages.sales.manageWarehousesInQuote;
            return ({
                key: item.id,
                data: item,
                cells: isNewProduct ?
                    {
                        [QuoteInformationViewProductsDataGridColumnNames.remove]:
                            <SaveOrRollback
                                column={false}
                                showText={false}
                                loading={updating}
                                show={{
                                    save:
                                        !!newProduct.product &&
                                        (!!newProduct.warehouse || !operatorAccessLevels.packages.sales.manageWarehousesInQuote)
                                        && ((operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(newProduct.quantity) : parseInt(newProduct.quantity)) ?? 0) !== 0
                                }}
                                onSave={(setLoading) => addNewProductToOrder(setLoading)}
                            />,
                        [QuoteInformationViewProductsDataGridColumnNames.warehouse]:
                            operatorAccessLevels.packages.sales.manageWarehousesInQuote
                                ? <TextField
                                    select
                                    value={lineWarehouseOptions?.find(w => w.code === newProduct.warehouse?.code) ?? newProduct.warehouse ?? ''}
                                    onChange={(e) => setNewProduct((prev) => ({...prev, warehouse: e.target.value}))}
                                    fullWidth
                                    className={'mr-2'}
                                    label={"Warehouse"}
                                    size={'small'}
                                    SelectProps={{
                                        renderValue: (selected) => selected ? `${selected.name} (${selected.code})` : '',
                                    }}
                                >
                                    {
                                        lineWarehouseOptions?.map(warehouseOption => (
                                            <MenuItem key={warehouseOption.code} value={warehouseOption}>
                                                {warehouseOption.name} ({warehouseOption.code})
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                                : null,
                        [QuoteInformationViewProductsDataGridColumnNames.partNo]:
                            <SearchSection
                                warehouses={lineWarehouseOptions}
                                onPartSelected={onNewProductSelected}
                                loading={updating}
                                variant={'outlined'}
                                label={'Product'}
                                size={'small'}
                                className={'mr-1'}
                                paper={false}
                                searchIcon={false}
                                InputProps={{
                                    sx: {
                                        paddingRight: `8px !important`,
                                    },
                                }}
                            />,
                        [QuoteInformationViewProductsDataGridColumnNames.quantity]:
                            <TextField
                                fullWidth
                                label={"Quantity"}
                                size={'small'}
                                value={newProduct.quantity?.toString() ?? ''}
                                onChange={onNewProductQuantityChanged}
                                InputProps={{
                                    inputProps: {
                                        ...(!operatorAccessLevels.packages.sales.issueCredit && {
                                            min: 0,
                                        })
                                    },
                                }}
                                type={'number'}
                            />,
                    }
                    : {
                        [QuoteInformationViewProductsDataGridColumnNames.remove]:
                            <IconButton
                                onPressed={() => getRemoveConfirmation(item)}
                                disabled={updating}
                                color={'paper.error'}
                                className={'remove-icon'}
                            >
                                {
                                    updating
                                        ? <CircularProgress size={20} color={'secondary'}/>
                                        : <SvgIcon component={RemoveIcon} className='icon normal'/>
                                }
                            </IconButton>,
                        [QuoteInformationViewProductsDataGridColumnNames.warehouse]: {
                            value: ({openEditPopover}) => <>
                                {
                                    warehouseEditable &&
                                    <IconButton
                                        color={'secondary'}
                                        onPressed={openEditPopover}
                                        disabled={updating}
                                    >
                                        {
                                            updating
                                                ? <CircularProgress size={20} color={'secondary'}/>
                                                : <Edit className={"icon small"}/>
                                        }
                                    </IconButton>
                                }
                                <p
                                    {...dataGridCellTextProps}
                                    className={classnames({'ml-2': warehouseEditable})}
                                >
                                    {!!warehouse ? `${warehouse?.name} (${warehouse?.code})` : item.whse ?? ""}
                                </p>
                            </>,
                            editable: warehouseEditable,
                            editableValue: warehouse,
                            EditableContentComponent:
                                <WarehouseEditor warehouses={lineWarehouseOptions}/>,
                        },
                        [QuoteInformationViewProductsDataGridColumnNames.partNo]:
                            <ProductInformationPopover
                                partNo={order?.appliedGiftCardInfo?.equalPartNo === item?.partNo ? null : item?.partNo}>
                                {item?.partNo ?? ""}
                            </ProductInformationPopover>,
                        [QuoteInformationViewProductsDataGridColumnNames.description]: {
                            value: ({openEditPopover}) => <>
                                {
                                    descriptionEditable &&
                                    <IconButton
                                        color={'secondary'}
                                        onPressed={openEditPopover}
                                        disabled={updating}
                                    >
                                        {
                                            updating
                                                ? <CircularProgress size={20} color={'secondary'}/>
                                                : <Edit className={"icon small"}/>
                                        }
                                    </IconButton>
                                }
                                <p
                                    {...dataGridCellTextProps}
                                    className={classnames({'ml-2': descriptionEditable})}
                                >
                                    {item.description}
                                </p>
                            </>,
                            editable: descriptionEditable,
                            editableValue: item.description,
                            EditableContentComponent: <DescriptionEditor/>,
                        },
                        [QuoteInformationViewProductsDataGridColumnNames.discount]: {
                            value: ({openEditPopover}) => <>
                                {
                                    discountEditable && operatorAccessLevels.packages.sales.manageQuotes &&
                                    <IconButton
                                        color={'secondary'}
                                        onPressed={openEditPopover}
                                        disabled={updating}
                                    >
                                        {
                                            updating
                                                ? <CircularProgress size={20} color={'secondary'}/>
                                                : <Edit className={"icon small"}/>
                                        }
                                    </IconButton>
                                }
                                <p
                                    {...dataGridCellTextProps}
                                    className={classnames({'ml-2': discountEditable && operatorAccessLevels.packages.sales.manageQuotes})}
                                >
                                    {item.discountPct ?? '0'} %
                                </p>
                            </>,
                            editable: discountEditable && operatorAccessLevels.packages.sales.manageQuotes,
                            editableValue: item.discountPct,
                            EditableContentComponent: <DiscountEditor/>,
                        },
                        [QuoteInformationViewProductsDataGridColumnNames.quantity]: {
                            value: ({openEditPopover}) =>
                                <>
                                    {
                                        !availability?.isComingSoon &&
                                        <>
                                            {
                                                quantityEditable &&
                                                <IconButton onPressed={() => openEditPopover()} disabled={updating}>
                                                    {
                                                        updating
                                                            ? <CircularProgress size={20} color={'secondary'}/>
                                                            : <Edit className={"icon small"}/>
                                                    }
                                                </IconButton>
                                            }
                                            <p {...dataGridCellTextProps} className={classnames({'ml-2': quantityEditable})}>
                                                {item.orderQty ?? '0'}
                                            </p>
                                        </>
                                    }
                                    {
                                        !!availability?.isComingSoon && warehouseCreatable &&
                                        <>
                                            <Button
                                                onClick={() => createItemInSelectedWarehouse(item, warehouse)}
                                                disabled={creatingItemInSelectedWarehouse.includes(item.partNo)}
                                                variant={'outlined'}
                                                color={'primary'}
                                            >
                                                {
                                                    creatingItemInSelectedWarehouse.includes(item.partNo)
                                                        ? "Creating in warehouse..."
                                                        : "Create in warehouse"
                                                }
                                            </Button>
                                        </>
                                    }
                                </>,
                            editable: quantityEditable && !availability?.isComingSoon,
                            editableValue: item.orderQty,
                            EditableContentComponent: <QuantityEditor/>,
                        },
                        [QuoteInformationViewProductsDataGridColumnNames.availableQuantity]:
                            productAvailabilities[item.partNo]?.loading
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : availability?.isComingSoon
                                    ? "Coming Soon"
                                    : availability?.availableQty ?? '--',
                        [QuoteInformationViewProductsDataGridColumnNames.unitPrice]: {
                            value: ({openEditPopover}) => <>
                                {
                                    unitPriceEditable &&
                                    <IconButton onPressed={() => openEditPopover()} disabled={updating}>
                                        {
                                            updating
                                                ? <CircularProgress size={20} color={'secondary'}/>
                                                : <Edit className={"icon small"}/>
                                        }
                                    </IconButton>
                                }
                                <p {...dataGridCellTextProps} className={classnames({'ml-2': unitPriceEditable})}>
                                    {formatMoney(item.unitPriceBeforeDiscount ?? 0)}
                                </p>
                            </>,
                            editable: unitPriceEditable,
                            editableValue: item.unitPriceBeforeDiscount ?? 0,
                            EditableContentComponent:
                                <UnitPriceEditor
                                    minValue={!eligiblePriceChangePct ? undefined : item.unitPriceBeforeDiscount - (item.unitPriceBeforeDiscount * (eligiblePriceChangePct ?? 0) / 100)}
                                    maxValue={!eligiblePriceChangePct ? undefined : item.unitPriceBeforeDiscount + (item.unitPriceBeforeDiscount * (eligiblePriceChangePct ?? 0) / 100)}
                                />,
                        },
                        [QuoteInformationViewProductsDataGridColumnNames.totalPrice]: (Number(item.unitPrice ?? '0') * (item.orderQty ?? 0)),
                        ...(EnvService.DEV_showOrderProductComments && {
                            [QuoteInformationViewProductsDataGridColumnNames.comment]:
                                commentEditable
                                    ? {
                                        value: ({openEditPopover}) => <>
                                            <IconButton
                                                color={'secondary'}
                                                onPressed={() => openEditPopover()}
                                                disabled={updating}
                                            >
                                                {
                                                    updating
                                                        ? <CircularProgress size={20} color={'secondary'}/>
                                                        : <Edit className={"icon small"}/>
                                                }
                                            </IconButton>
                                            <p {...dataGridCellTextProps} className={'ml-2'}>
                                                {item?.comment?.trim() ? item.comment : '--'}
                                            </p>
                                        </>,
                                        editable: true,
                                        editableValue: item?.comment ?? '',
                                        EditableContentComponent: <CommentEditor/>,
                                    }
                                    : (item?.comment?.trim() ? item.comment : '--'),
                        }),
                    },
            })
        })
    }, [order?.items, order?.isClosed, order?.appliedGiftCardInfo?.equalPartNo, updating, operatorAccessLevels.packages.sales.manageQuotes, operatorAccessLevels.packages.sales.manageWarehousesInQuote, operatorAccessLevels.packages.sales.manageUnitPriceInQuote, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders, operatorAccessLevels.packages.sales.issueCredit, canEditUnitPrice, warehouses, productAvailabilities, newProduct.product, newProduct.warehouse, newProduct.quantity, onNewProductSelected, onNewProductQuantityChanged, eligiblePriceChangePct, addNewProductToOrder, getRemoveConfirmation, creatingItemInSelectedWarehouse, createItemInSelectedWarehouse, getWarehouseOptionsForQuantity])

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center'>
                <Paper className='paper w-100 px-3 py-2'>
                    <Row className='no-gutters align-items-center justify-content-between w-100'>
                        {
                            !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes &&
                            <>
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%'}}>
                                    <SearchSection
                                        onPartSelected={addProductToOrder}
                                        loading={updating || checkingFormQualification}
                                        removeAfterSelect
                                        warehouses={warehouses}
                                        showWarehouses={operatorAccessLevels.packages.sales.manageWarehousesInQuote}
                                        variant={'outlined'}
                                        size={'small'}
                                        InputProps={{
                                            sx: {
                                                paddingRight: `8px !important`,
                                            },
                                        }}
                                    />
                                    <Button
                                        disabled={orderLoading || updating || checkingFormQualification}
                                        onClick={openImportWithAiDialog}
                                        className={'d-flex align-items-center'}
                                        color={'primary'}
                                        variant={'outlined'}
                                        sx={{
                                            minWidth: 165,
                                            height: 50,
                                        }}
                                    >
                                        <AutoFixHigh sx={{marginInlineEnd: 0.5}} className={'icon small'}/>
                                        {
                                            updating
                                                ? "Updating..."
                                                : "Import with AI"
                                        }
                                    </Button>
                                </Box>
                                <Divider className='divider mt-2'/>
                            </>
                        }
                        <SuggestiveSellingSectionPending pendingKey={quotePendingKey}/>
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            columns={dataGridColumns}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            hideFooter
                            hideExporting
                            visibleRows={10}
                            rows={dataGridRows}
                            state={{
                                loading: {state: orderLoading || updating},
                                density: dataGridDensity,
                            }}
                            onCellEdited={onCellEdited}
                        />
                        <div className='my-2 w-100'/>
                    </Row>
                </Paper>
            </Row>
            <ImportProductsWithAIDialog
                {...importWithAiDialog}
            />
            {GateDialogs}
        </>
    )
}

const DiscountEditor = ({value, setValue, setCanSubmitChanges}) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        const parsed = Number(value)
        setCanSubmitChanges(!Number.isNaN(parsed));
    }, [setCanSubmitChanges, value])

    /**
     * Changes the value of this editor.
     */
    const onValueChanged = (e) => {
        const parsed = Number(e.target.value)
        if (!e.target.value || Number.isNaN(parsed))
            return setValue(undefined);
        setValue(Math.max(0, Math.min(parsed, 100)))
    }

    return (
        <>
            <TextField
                fullWidth
                size={'small'}
                label={"New Discount %"}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                type={'number'}
                inputProps={{
                    min: 0,
                    max: 100,
                }}
            />
        </>
    );
}

const QuantityEditor = ({value, setValue, setCanSubmitChanges}) => {
    const operatorAccessLevels = useAccessLevels();

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        const parsed = Number(value)
        setCanSubmitChanges(!Number.isNaN(parsed) && parsed !== 0);
    }, [setCanSubmitChanges, value])

    /**
     * Changes the value of this editor.
     *
     * - ensures the value is non-negative.
     * @param value
     */
    const onValueChanged = useCallback((e) => {
        let parsed = Number(e.target.value)
        if (!e.target.value || Number.isNaN(parsed))
            return setValue(undefined);
        if (!operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders) {
            parsed = Math.trunc(parsed)
        }
        if (!operatorAccessLevels.packages.sales.issueCredit) {
            parsed = Math.max(0, parsed);
        }
        setValue(parsed);
    }, [setValue, operatorAccessLevels.packages.sales.issueCredit, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    return (
        <>
            <TextField
                fullWidth
                label={"New Quantity"}
                size={'small'}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                type={'number'}
                error={!(!Number.isNaN(value) && Number(value) !== 0)}
                helperText={
                    Number(value) === 0
                        ? "Quantity must be non-zero."
                        : undefined
                }
            />
        </>
    );
}

const UnitPriceEditor = ({value, setValue, setCanSubmitChanges, minValue, maxValue}) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        const parsed = Number(value)
        setCanSubmitChanges(!Number.isNaN(parsed) && parsed >= 0);
    }, [setCanSubmitChanges, value])

    /**
     * Changes the value of this editor.
     *
     * - ensures the value is non-negative.
     * @param value
     */
    const onValueChanged = useCallback((e) => {
        const parsed = Number(e.target.value)
        if (!e.target.value || Number.isNaN(parsed))
            return setValue(undefined);
        setValue(parsed);
    }, [setValue])

    /**
     * Changes the value of this editor.
     * @type {(function(*): (*|undefined))}
     */
    const onValueBlurred = useCallback((e) => {
        if (!e.target.value)
            return setValue(undefined);
        let v = Math.max(parseFloat(e.target.value), 0);
        if (minValue !== undefined && v < minValue) {
            v = minValue;
        } else if (maxValue !== undefined && v > maxValue) {
            v = maxValue;
        }
        setValue(v);
    }, [setValue])

    return (
        <>
            <TextField
                fullWidth
                label={"Unit Price"}
                size={'small'}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                onBlur={onValueBlurred}
                inputProps={{
                    min: 0,
                }}
                type={'number'}
                helperText={
                    [
                        !!minValue ? `Min is ${formatMoney(minValue)}` : '',
                        !!maxValue ? `Max is ${formatMoney(maxValue)}` : '',
                    ].filter(e => !!e).join(' and ') + (minValue || maxValue ? '.' : "")
                }
            />
        </>
    );
}

const CommentEditor = ({value, setValue, setCanSubmitChanges}) => {
    useLayoutEffect(() => {
        setCanSubmitChanges(true);
    }, [setCanSubmitChanges]);

    const onValueChanged = useCallback((e) => {
        setValue(e.target.value);
    }, [setValue]);

    return (
        <TextField
            fullWidth
            label={"Comment"}
            size={'small'}
            multiline
            minRows={3}
            value={value?.toString() ?? ''}
            onChange={onValueChanged}
            sx={{
                minWidth: 280,
            }}
        />
    );
}

const DescriptionEditor = ({value, setValue, setCanSubmitChanges}) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        setCanSubmitChanges(!!value?.trim());
    }, [setCanSubmitChanges, value])

    /**
     * Changes the value of this editor.
     *
     * - ensures the value is non-negative.
     * @param value
     */
    const onValueChanged = useCallback((e) => {
        setValue(e.target.value);
    }, [setValue])

    return (
        <>
            <TextField
                fullWidth
                label={"Description"}
                size={'small'}
                multiline
                minRows={4}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                sx={{
                    minWidth: 300,
                }}
            />
        </>
    );
}

const WarehouseEditor = ({value, setValue, setCanSubmitChanges, warehouses}) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        setCanSubmitChanges(!!value);
    }, [setCanSubmitChanges, value])

    return (
        <TextField
            sx={{minWidth: 200}}
            select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            label={"Warehouse"}
            size={'small'}
            SelectProps={{
                renderValue: (selected) => selected ? `${selected.name} (${selected.code})` : '',
            }}
        >
            {
                warehouses?.map(warehouse => (
                    <MenuItem key={warehouse.code} value={warehouse}>
                        {warehouse.name} ({warehouse.code})
                    </MenuItem>
                )) ?? []
            }
        </TextField>
    );
}


const SearchSection = ({
                           showWarehouses,
                           warehouses,
                           onPartSelected,
                           removeAfterSelect,
                           loading: loadingProps,
                           ...props
                       }) => {
    const [loading, setLoading] = useState(false);
    const [autoCompleteText, setAutoCompleteText] = useState('');
    const [autoCompleteValue, setAutoCompleteValue] = useState(null);
    const [autoCompleteParts, setAutoCompleteParts] = useState([]);
    const isMounted = useIsMounted();

    const [warehousesPopoverAnchor, setWarehousesPopoverAnchor] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);

    /**
     * Sets the selected warehouse as the default warehouse if there is no selected warehouse.
     */
    useEffect(() => {
        if (warehouses?.length && !selectedWarehouse) {
            setSelectedWarehouse(warehouses?.find(e => e.code === '00') ?? warehouses[0]);
        }
    }, [warehouses, selectedWarehouse])

    /**
     * Resets the autoCompleteValue and calls the onPartSelected with null.
     */
    useEffect(() => {
        if (!autoCompleteText) {
            setAutoCompleteValue(null);
            onPartSelected(null);
        }
    }, [autoCompleteText, onPartSelected]);

    /**
     * Gets the parts that have the keywords in their partNo or description by calling the api.
     * @param keywords {string}
     */
    const getAutoCompleteParts = useCallback((keywords) => {
        setLoading(true);
        Api.autoCompletePartLookup(keywords).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setAutoCompleteParts(response.data ?? []);
            }
            setLoading(false);
        })
    }, [isMounted])

    /**
     * Listens for the changes in the text of the autoComplete and with each change, sets a timer.
     * By the end of this timer, gets the parts for the text from api.
     */
    useEffect(() => {
        if (!autoCompleteText?.length || autoCompleteText?.length < 3) return;
        const timer = setTimeout(() => {
            if (!isMounted()) {
                return;
            }
            if (!autoCompleteText?.length || autoCompleteText?.length < 3) return;
            getAutoCompleteParts(autoCompleteText)
        }, 500);
        return () => clearTimeout(timer)
    }, [autoCompleteText, getAutoCompleteParts, isMounted])

    /**
     * Assigns the Part with respect to its type.
     * If string => must be a text, sets the auto complete text so the values get filtered,
     * if object => must be a part, calls the api to add the part to the quote.
     * @param _ {MouseEvent}
     * @param part {any}
     */
    const onSelectNewPart = (_, part) => {
        if (!part) part = ''
        if (typeof part === 'string') {
            setAutoCompleteText(part)
        } else if (typeof part === 'object') {
            onPartSelected(part.value, selectedWarehouse);
            if (removeAfterSelect) {
                setAutoCompleteValue(null);
                setAutoCompleteText('');
            } else {
                setAutoCompleteText(part.value.partNumber);
                setAutoCompleteValue(part.value)
            }
        }
    }

    /**
     * Renders the Parts in the menu of the partAutoComplete
     *
     * @param _
     * @param option
     * @return {JSX.Element|string}
     */
    const renderAutoCompletePart = (_, option) => {
        if (!option) {
            return '';
        }
        if (typeof option === 'string') {
            return (
                <p className='mx-2 p-2'>
                    {option}
                </p>
            );
        }
        return (
            <Row className='no-gutters align-items-center w-100 justify-content-start py-2'>
                <p className='text-left text-sm text-color-1 mr-5'>
                    {option.value?.partNumber}
                </p>
            </Row>
        )
    }

    return (
        <>
            <SearchAutoComplete
                label={'Search'}
                {...props}
                value={autoCompleteValue}
                loading={loading || loadingProps}
                onSelect={onSelectNewPart}
                getOptionLabel={(option) => option?.value?.partNumber}
                onChange={(e) => setAutoCompleteText(e.target.value)}
                clearOnBlur={removeAfterSelect}
                disabled={loadingProps}
                freeSolo={false}
                type={'text'}
                inputValue={autoCompleteText}
                options={autoCompleteParts?.map(part => ({
                    title: `${part.partNumber}`,
                    value: part
                }))}
                renderOption={renderAutoCompletePart}
                loadingText={
                    <div className='loading auto-complete'>
                        <div/>
                        <div/>
                        <div/>
                        <div/>
                    </div>
                }
                InputProps={{
                    ...props.InputProps,
                    startAdornment:
                        showWarehouses &&
                        <Button
                            disabled={loadingProps || !warehouses.length}
                            sx={{
                                backgroundColor: 'background.default',
                                marginInlineEnd: 1,
                                height: 34,
                                minWidth: 34,
                                width: 34,
                                color: 'primary.main',
                            }}
                            onClickCapture={(e) => setWarehousesPopoverAnchor(e.currentTarget)}
                        >
                            {selectedWarehouse?.code ?? "--"}
                        </Button>
                }}
            />
            <Popover
                open={!!warehousesPopoverAnchor}
                onClose={() => setWarehousesPopoverAnchor(null)}
                anchorEl={warehousesPopoverAnchor}
                elevation={1}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        width: 200,
                        padding: 1,
                    }}
                >
                    <Typography variant={'body2'} fontWeight={600} sx={{marginBlockEnd: 1}}>
                        Select your Warehouse
                    </Typography>
                    {
                        warehouses?.map((e) => (
                            <MenuItem
                                key={e.code}
                                value={e}
                                selected={selectedWarehouse?.code === e?.code}
                                onClick={() => {
                                    setWarehousesPopoverAnchor(null);
                                    setSelectedWarehouse(e);
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    width: 'fill-available',
                                    borderRadius: 1,
                                    marginInline: 0,
                                    padding: 1,
                                    '.fill': {
                                        color: 'primary.main',
                                    }
                                }}
                            >
                                <Typography variant={'caption'}>
                                    {e.name ?? ""} <Typography
                                    sx={{display: 'inline-block'}} variant={'body2'} color={'primary'} fontWeight={600}
                                >
                                    ({e.code})
                                </Typography>
                                </Typography>
                            </MenuItem>
                        ))
                    }
                </Box>
            </Popover>
        </>
    );
}

export default QuoteInformationViewProductsSection;
